var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../../sutl';
import { AsciiText } from './AsciiText';
import { CommandBuffer } from './CommandBuffer';
import { CommandFormatter } from './CommandFormatter';
import { Observable } from './Observable';
import { Socket } from './Socket';
import { SocketEmulator } from './SocketEmulator';
import { Timeouts } from './Timeouts';
import { isPlainAsyncHandler, isRegExpAsyncHandler, TransportError, } from './Transport';
export const defaultSocketOptions = {
    allowNoConnection: false,
    commandExtractor: {
        terminators: ['\r', '\n'],
        allowEmptyCommands: false,
    },
    idleTimeoutMs: 20000,
    isBinary: false,
    logReceivedData: false,
    negotiateTelnet: false,
    port: 23,
    sendTerminator: '\r',
    sendTimeoutMs: 2000,
    socketType: 'plain',
};
function formatTags(tags) {
    if (tags.length === 0)
        return '';
    return ` [${tags.join(', ')}]`;
}
export class CommandSocket {
    constructor(logFactory, options, emulatorKey) {
        // bind callbacks
        this.logFactory = logFactory;
        this.emulatorKey = emulatorKey;
        this.asyncHandlerId = 1;
        this.asyncHandlers = {};
        this.checkAllHandlers = false;
        this.connectionLostObservable = new Observable();
        this.isProcessingData = false;
        this.isReconnecting = false;
        this.lastReceivedTime = Date.now();
        this.retryAttempts = 0;
        this.queuedCommands = new Array();
        this.timeouts = new Timeouts();
        this.options = Object.assign(Object.assign({}, defaultSocketOptions), { parser: (command) => command });
        this.onIdleTimeout = this.onIdleTimeout.bind(this);
        this.processReceivedData = this.processReceivedData.bind(this);
        this.sendNextCommand = this.sendNextCommand.bind(this);
        this.sendTimeoutCallback = this.sendTimeoutCallback.bind(this);
        this.suspendTimeoutCallback = this.suspendTimeoutCallback.bind(this);
        // initialization
        this.idleTimeout = this.timeouts.create();
        this.options = Object.assign(this.options, options ? options : {});
        this.commandBuffer = new CommandBuffer(this.options.commandExtractor);
        this.log = logFactory.channel('CommandSocket');
        this.driverLog = logFactory.channel('CommandSocket', 'driver');
        this.sendTimeout = this.timeouts.create();
        this.suspendTimeout = this.timeouts.create();
        if (emulatorKey !== undefined) {
            this._emulator = new SocketEmulator(emulatorKey, this.logFactory, this.options.commandExtractor, this.options.sendTerminator, this.options.isBinary);
        }
    }
    // getters
    get emulator() {
        var _a;
        return (_a = this._emulator) === null || _a === void 0 ? void 0 : _a.emulator;
    }
    get isSuspended() {
        return this._suspendType !== undefined;
    }
    get port() {
        return this.options.port;
    }
    get state() {
        var _a, _b;
        return (_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.state) !== null && _b !== void 0 ? _b : 'disconnected';
    }
    get suspendType() {
        return this._suspendType;
    }
    // public methods
    connect(host, username, password) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info(`connecting to ${host}:${this.options.port}`);
            if (password === undefined)
                password = '';
            this.reconnectInfo = { host, username, password };
            try {
                if (this.socket === undefined)
                    this.socket = this.createSocket(this.emulatorKey);
                if (this.options.socketType === 'plain') {
                    yield this.socket.connect(host, this.options.port);
                }
                else {
                    // specify username and password for ssh
                    yield this.socket.connect(host, this.options.port, username, password);
                }
            }
            catch (error) {
                if (this.options.allowNoConnection) {
                    this.log.info(`connect failed, error: ${error.message}`);
                }
                else {
                    throw error;
                }
            }
            if (((_a = this.socket) === null || _a === void 0 ? void 0 : _a.state) === 'connected' && this.options.idleTimeoutMs > 0) {
                this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
            }
        });
    }
    createSocket(emulatorKey) {
        let socket;
        if (emulatorKey) {
            if (this._emulator === undefined)
                throw new Error('emulator undefined');
            socket = this._emulator;
        }
        else {
            socket = new Socket(this.logFactory);
        }
        socket.onConnectionLost(() => {
            var _a;
            if (socket.key !== ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.key)) {
                this.log.info(`ingoring connection lost for old socket: ${socket.key}`);
                return;
            }
            this.connectionLostCallback();
        });
        socket.onData((data) => {
            var _a;
            if (socket.key !== ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.key)) {
                this.log.info(`ingoring data for old socket: ${socket.key}`);
                return;
            }
            this.dataCallback(data);
        });
        socket.negotiateTelnet = this.options.negotiateTelnet;
        this.log.info(`created socket: ${socket.key}`);
        return socket;
    }
    disconnect() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info('disconnecting');
            const id = `${(_a = this.socket) === null || _a === void 0 ? void 0 : _a.host}:${(_b = this.socket) === null || _b === void 0 ? void 0 : _b.port}`;
            const err = new TransportError(`${id}: socket disconnected`, 'disconnected');
            this.sendTimeout.clear();
            if (this.currentCommand) {
                this.currentCommand.reject(err);
            }
            for (const qc of this.queuedCommands) {
                qc.reject(err);
            }
            yield ((_c = this.socket) === null || _c === void 0 ? void 0 : _c.disconnect());
            this.reset();
        });
    }
    // Using the state getter is preferred, but sometimes a method is necessary to get around
    // Typescript errors, e.g. https://github.com/microsoft/TypeScript/issues/29155
    getState() {
        var _a, _b;
        return (_b = (_a = this.socket) === null || _a === void 0 ? void 0 : _a.state) !== null && _b !== void 0 ? _b : 'disconnected';
    }
    hasCommands(tag) {
        if (tag === undefined) {
            return this.queuedCommands.length > 0;
        }
        else if (typeof tag === 'string') {
            for (const qc of this.queuedCommands) {
                if (qc.tags.includes(tag))
                    return true;
            }
        }
        else {
            for (const tagElement of tag) {
                if (this.hasCommands(tagElement))
                    return true;
            }
        }
        return false;
    }
    onConnectionLost(connectionLostCallback) {
        this.connectionLostObservable.subscribe(connectionLostCallback);
        return this;
    }
    reconnect() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info('reconnecting');
            if (this.isReconnecting)
                throw new Error('already reconnecting');
            this.isReconnecting = true;
            let reconnected = false;
            if (this.state === 'connected') {
                const err = new TransportError('socket disconnected', 'disconnected');
                if (this.currentCommand) {
                    this.currentCommand.reject(err);
                }
                for (const qc of this.queuedCommands) {
                    qc.reject(err);
                }
                yield ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.disconnect());
            }
            else if (this.state !== 'disconnected') {
                throw new Error(`can't reconnect while ${this.state}`);
            }
            if (this.reconnectInfo !== undefined) {
                try {
                    this.socket = this.createSocket(this.emulatorKey);
                    yield this.connect(this.reconnectInfo.host, this.reconnectInfo.username, this.reconnectInfo.password);
                    reconnected = true;
                }
                catch (error) {
                    this.log.info(error.message);
                }
            }
            else {
                this.log.error('reconnectInfo undefined');
            }
            this.reset();
            this.isReconnecting = false;
            if (!reconnected && !this.options.allowNoConnection) {
                this.connectionLostObservable.notifyListeners();
            }
            return reconnected;
        });
    }
    registerAsync(handlerOrRegExp, callback, name) {
        const id = (this.asyncHandlerId++).toString();
        if (handlerOrRegExp instanceof RegExp) {
            if (callback === undefined)
                throw new Error('callback undefined');
            this.asyncHandlers[id] = {
                regExp: handlerOrRegExp,
                callback,
            };
            if (name)
                this.asyncHandlers[id].name = name;
        }
        else {
            if (isRegExpAsyncHandler(handlerOrRegExp) || isPlainAsyncHandler(handlerOrRegExp)) {
                this.asyncHandlers[id] = handlerOrRegExp;
            }
            else {
                throw new Error('unexpected async handler type');
            }
        }
        return id;
    }
    removeCommands(tag) {
        if (tag === undefined) {
            this.log.debug('removed all commands');
            const error = new TransportError('removed all commands', 'commandRemoved');
            for (const qc of this.queuedCommands) {
                qc.reject(error);
            }
            this.queuedCommands.length = 0;
        }
        else if (typeof tag === 'string') {
            this.log.debug(`removing: ${tag}`);
            const errorMessage = `removed command with tag: ${tag}`;
            Sutl.filterInPlace(this.queuedCommands, qc => {
                if (qc.tags.includes(tag)) {
                    qc.reject(new TransportError(errorMessage, 'commandRemoved'));
                    return false;
                }
                return true;
            });
        }
        else {
            for (const tagElement of tag) {
                this.removeCommands(tagElement);
            }
        }
    }
    reset() {
        this.commandBuffer.clear();
        this.currentCommand = undefined;
        this.isProcessingData = false;
        this.queuedCommands.length = 0;
        this.savedUnterminatedRegExp = undefined;
        this._suspendType = undefined;
        this.timeouts.clearAll();
    }
    resume() {
        this.suspendTimeout.clear();
        this.suspendTimeoutCallback();
    }
    send(command, regExpOrFn, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            command += this.options.sendTerminator;
            return this.sendInternal(command, regExpOrFn, tagsOrOpts, opts);
        });
    }
    sendAsync(command, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            command += this.options.sendTerminator;
            return this.sendInternalAsync(command, tagsOrOpts, opts);
        });
    }
    sendRaw(data, regExpOrFn, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.sendInternal(data, regExpOrFn, tagsOrOpts, opts);
        });
    }
    sendRawAsync(data, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.sendInternalAsync(data, tagsOrOpts, opts);
        });
    }
    setIdleTimeoutMs(idleTimeoutMs) {
        if (idleTimeoutMs === this.options.idleTimeoutMs)
            return;
        this.options.idleTimeoutMs = idleTimeoutMs;
        if (this.idleTimeout.isRunning) {
            this.idleTimeout.clear();
            if (this.options.idleTimeoutMs > 0) {
                this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
            }
        }
    }
    setUnterminatedRegExp(regExp) {
        return this.commandBuffer.setUnterminatedRegExp(regExp);
    }
    suspend(delayMs, suspendType) {
        this.idleTimeout.clear();
        this.suspendTimeout.clear();
        this._suspendType = suspendType;
        if (delayMs === undefined) {
            // suspend indefinitely
        }
        else if (delayMs <= 0) {
            this.log.error(`suspend: delayMs must be > 0`);
            this.resume();
        }
        else {
            // suspend for specified delayMs
            this.suspendTimeout.set(this.suspendTimeoutCallback, delayMs);
        }
    }
    unregisterAsync(id) {
        if (id in this.asyncHandlers) {
            delete this.asyncHandlers[id];
            return true;
        }
        return false;
    }
    waitFor(regExpOrFn, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                var _a;
                let id = '';
                let savedUnterminatedRegExp = false;
                if (opts !== undefined && 'unterminatedRegExp' in opts) {
                    savedUnterminatedRegExp = true;
                    this.saveUnterminatedRegExp(opts.unterminatedRegExp);
                }
                const timeout = this.timeouts.set(() => {
                    this.unregisterAsync(id);
                    if (savedUnterminatedRegExp)
                        this.restoreUnterminatedRegExp();
                    reject(new TransportError('receive timeout', 'receiveTimeout'));
                }, (_a = opts === null || opts === void 0 ? void 0 : opts.receiveTimeoutMs) !== null && _a !== void 0 ? _a : this.options.sendTimeoutMs);
                if (typeof regExpOrFn === 'function') {
                    id = this.registerAsync({
                        match: regExpOrFn,
                        callback: (resp) => {
                            this.unregisterAsync(id);
                            if (savedUnterminatedRegExp)
                                this.restoreUnterminatedRegExp();
                            timeout.clear();
                            resolve(resp);
                        },
                        name: opts === null || opts === void 0 ? void 0 : opts.name,
                    });
                }
                else {
                    id = this.registerAsync(regExpOrFn, match => {
                        this.unregisterAsync(id);
                        if (savedUnterminatedRegExp)
                            this.restoreUnterminatedRegExp();
                        timeout.clear();
                        resolve(match);
                    }, opts === null || opts === void 0 ? void 0 : opts.name);
                }
            });
        });
    }
    // private methods
    connectionLostCallback() {
        var _a, _b;
        if (this.isReconnecting)
            return;
        this.log.info('connection lost');
        const id = `${(_a = this.socket) === null || _a === void 0 ? void 0 : _a.host}:${(_b = this.socket) === null || _b === void 0 ? void 0 : _b.port}`;
        const err = new TransportError(`${id}: connection lost`, 'connectionLost');
        if (this.currentCommand) {
            this.currentCommand.reject(err);
        }
        for (const qc of this.queuedCommands) {
            qc.reject(err);
        }
        this.reset();
        this.connectionLostObservable.notifyListeners();
    }
    dataCallback(data) {
        var _a;
        if (this.options.logReceivedData) {
            this.log.debug(`received: ${CommandFormatter.format(AsciiText.decode(data), true)}`);
        }
        if (((_a = this.socket) === null || _a === void 0 ? void 0 : _a.state) !== 'connected')
            return;
        this.commandBuffer.add(AsciiText.decode(data));
        this.processReceivedData();
    }
    onIdleTimeout() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state === 'connected') {
                this.log.warning('idle timeout, disconnecting');
                try {
                    yield this.disconnect();
                }
                catch (error) {
                    this.log.error(error.message);
                }
            }
            else {
                this.log.warning(`idle timeout while ${this.state}`);
            }
        });
    }
    processReceivedData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== 'connected' || this.isProcessingData)
                return;
            if (this._suspendType === 'all')
                return;
            const preCommand = this.commandBuffer.getCommand();
            if (preCommand === undefined) {
                this.isProcessingData = false;
                return;
            }
            if (this._suspendType === undefined && this.options.idleTimeoutMs > 0) {
                this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
            }
            const command = this.options.parser(preCommand);
            const fmtCmd = typeof command === 'string'
                ? CommandFormatter.format(command, this.options.isBinary)
                : JSON.stringify(command !== null && command !== void 0 ? command : preCommand);
            if (command === undefined) {
                this.driverLog.info(`ignoring: ${fmtCmd}`);
                this.isProcessingData = false;
                this.timeouts.set(this.processReceivedData); // prevent deep nesting
                return;
            }
            this.isProcessingData = true;
            this.lastReceivedTime = Date.now();
            let handled = false;
            // First, check current synchronous handler.
            if (this.currentCommand) {
                const response = this.currentCommand.match(command);
                if (response !== undefined) {
                    const tags = formatTags(this.currentCommand.tags);
                    this.driverLog.debug(`processing${tags}: ${fmtCmd}`);
                    if (Array.isArray(response)) {
                        const cmd = this.currentCommand;
                        cmd.resolve(response);
                    }
                    else {
                        const cmd = this.currentCommand;
                        cmd.resolve(response);
                    }
                    this.currentCommand = undefined;
                    handled = true;
                }
            }
            // If that didn't match, then check the asynchronous handlers.
            if (!handled) {
                for (const handler of Object.values(this.asyncHandlers)) {
                    if (isRegExpAsyncHandler(handler)) {
                        const response = handler.regExp.exec(command);
                        if (response) {
                            const name = handler.name ? ` [${handler.name}]` : '';
                            this.driverLog.debug(`processing${name}: ${fmtCmd}`);
                            handler.callback(response);
                            handled = true;
                            if (!this.checkAllHandlers)
                                break;
                        }
                    }
                    else {
                        const response = handler.match(command);
                        if (response !== undefined) {
                            const name = handler.name ? ` [${handler.name}]` : '';
                            this.driverLog.debug(`processing${name}: ${fmtCmd}`);
                            handler.callback(response);
                            handled = true;
                            if (!this.checkAllHandlers)
                                break;
                        }
                    }
                }
            }
            if (!handled) {
                this.driverLog.debug(`unhandled: ${fmtCmd}`);
            }
            this.isProcessingData = false;
            this.timeouts.set(this.processReceivedData); // prevent deep nesting
            this.timeouts.set(this.sendNextCommand); // in case command sent while waiting for this one
        });
    }
    restoreUnterminatedRegExp() {
        if (this.savedUnterminatedRegExp !== undefined) {
            this.commandBuffer.setUnterminatedRegExp(this.savedUnterminatedRegExp === 'none' ? undefined : this.savedUnterminatedRegExp);
            this.savedUnterminatedRegExp = undefined;
        }
    }
    saveUnterminatedRegExp(regExp) {
        const savedUnterminatedRegExp = this.commandBuffer.setUnterminatedRegExp(regExp);
        this.savedUnterminatedRegExp = savedUnterminatedRegExp !== null && savedUnterminatedRegExp !== void 0 ? savedUnterminatedRegExp : 'none';
        return savedUnterminatedRegExp;
    }
    sendInternal(data, regExpOrFn, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            let tags = [];
            if (typeof tagsOrOpts === 'string') {
                tags.push(tagsOrOpts);
            }
            else if (Array.isArray(tagsOrOpts)) {
                tags = tagsOrOpts;
            }
            else {
                opts = tagsOrOpts;
            }
            if (this.state !== 'connected') {
                const msg = `can't send while ${this.state}` +
                    `, tags = ${JSON.stringify(tags)}` +
                    `, data = ${CommandFormatter.format(data, this.options.isBinary)}`;
                this.log.debug(msg);
                throw new TransportError(msg, 'cantSend');
            }
            let qc;
            const promise = new Promise((resolve, reject) => {
                var _a, _b, _c, _d;
                if (regExpOrFn instanceof RegExp) {
                    qc = Object.assign({ command: data, match: (resp) => {
                            const match = regExpOrFn.exec(resp);
                            return match === null ? undefined : match;
                        }, reject,
                        resolve, retryAttempts: (_a = opts === null || opts === void 0 ? void 0 : opts.retryAttempts) !== null && _a !== void 0 ? _a : 0, sendTimeoutMs: (_b = opts === null || opts === void 0 ? void 0 : opts.sendTimeoutMs) !== null && _b !== void 0 ? _b : this.options.sendTimeoutMs, tags }, (opts !== undefined && 'unterminatedRegExp' in opts
                        ? { unterminatedRegExp: opts.unterminatedRegExp }
                        : undefined));
                }
                else {
                    qc = Object.assign({ command: data, match: regExpOrFn, reject,
                        resolve, retryAttempts: (_c = opts === null || opts === void 0 ? void 0 : opts.retryAttempts) !== null && _c !== void 0 ? _c : 0, sendTimeoutMs: (_d = opts === null || opts === void 0 ? void 0 : opts.sendTimeoutMs) !== null && _d !== void 0 ? _d : this.options.sendTimeoutMs, tags }, (opts !== undefined && 'unterminatedRegExp' in opts
                        ? { unterminatedRegExp: opts.unterminatedRegExp }
                        : undefined));
                }
                this.queuedCommands.push(qc);
                this.sendNextCommand();
            });
            if (this.queuedCommands.length > 0) {
                const cmd = CommandFormatter.format(data, this.options.isBinary);
                const strTags = tags.length > 0 ? ` [${tags.join(', ')}]` : '';
                this.driverLog.debug(`queued${strTags}: ${cmd}`);
            }
            return promise;
        });
    }
    sendInternalAsync(data, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            let tags = [];
            if (typeof tagsOrOpts === 'string') {
                tags.push(tagsOrOpts);
            }
            else if (Array.isArray(tagsOrOpts)) {
                tags = tagsOrOpts;
            }
            else {
                opts = tagsOrOpts;
            }
            if (this.state !== 'connected') {
                throw new Error(`can't send while ${this.state}` +
                    `, tags = ${JSON.stringify(tags)}` +
                    `, data = ${CommandFormatter.format(data, this.options.isBinary)}`);
            }
            let qc;
            const promise = new Promise((resolve, reject) => {
                var _a, _b;
                qc = Object.assign({ command: data, reject,
                    resolve, retryAttempts: (_a = opts === null || opts === void 0 ? void 0 : opts.retryAttempts) !== null && _a !== void 0 ? _a : 0, sendTimeoutMs: (_b = opts === null || opts === void 0 ? void 0 : opts.sendTimeoutMs) !== null && _b !== void 0 ? _b : this.options.sendTimeoutMs, tags }, (opts !== undefined && 'unterminatedRegExp' in opts
                    ? { unterminatedRegExp: opts.unterminatedRegExp }
                    : undefined));
                this.queuedCommands.push(qc);
                this.sendNextCommand();
            });
            if (this.queuedCommands.length > 0) {
                const cmd = CommandFormatter.format(data, this.options.isBinary);
                const strTags = tags.length > 0 ? ` [${tags.join(', ')}]` : '';
                this.driverLog.debug(`queued${strTags}: ${cmd}`);
            }
            return promise;
        });
    }
    sendNextCommand(command) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== 'connected' || this.currentCommand)
                return;
            if (this._suspendType === 'all')
                return;
            if (command === undefined) {
                command = this.queuedCommands.shift();
                this.retryAttempts = 0;
            }
            if (!command)
                return;
            if ('unterminatedRegExp' in command) {
                this.saveUnterminatedRegExp(command.unterminatedRegExp);
            }
            if ('match' in command) {
                this.currentCommand = command;
                this.sendTimeout.set(this.sendTimeoutCallback, command.sendTimeoutMs);
                // If sendTimeoutMs has been overridden for this command and the idle timeout will
                // expire before the send timeout, then increase the idle timeout, but also limit the
                // extension of the idle timeout so it can't be defeated by continuously sending
                // commands with sendTimeoutMs overrides.
                const now = Date.now();
                const maxIdleTime = this.options.idleTimeoutMs + 3 * command.sendTimeoutMs;
                if (this._suspendType === undefined &&
                    this.options.idleTimeoutMs > 0 &&
                    command.sendTimeoutMs > this.options.sendTimeoutMs &&
                    this.idleTimeout.expires - now < command.sendTimeoutMs &&
                    now - this.lastReceivedTime < maxIdleTime) {
                    const idleTimeoutMs = command.sendTimeoutMs + 1.1 * this.options.sendTimeoutMs;
                    this.idleTimeout.set(this.onIdleTimeout, idleTimeoutMs);
                }
            }
            const cmd = CommandFormatter.format(command.command, this.options.isBinary);
            const strTags = command.tags.length > 0 ? ` [${command.tags.join(', ')}]` : '';
            this.driverLog.debug(`sending${strTags}: ${cmd}`);
            try {
                if (this.socket === undefined)
                    throw new Error('socket undefined');
                yield this.socket.send(AsciiText.encode(command.command));
                if (!('match' in command)) {
                    this.restoreUnterminatedRegExp();
                    command.resolve();
                    this.timeouts.set(this.sendNextCommand); // prevent deep nesting
                }
            }
            catch (error) {
                this.log.warning(`send failed: ${error.message}`);
                this.sendTimeout.clear();
                this.restoreUnterminatedRegExp();
                this.currentCommand = undefined;
                this.timeouts.set(this.sendNextCommand); // prevent deep nesting
                command.reject(new TransportError(error.message, 'sendFailed'));
            }
        });
    }
    sendTimeoutCallback() {
        if (this.currentCommand === undefined)
            return;
        const qc = this.currentCommand;
        this.currentCommand = undefined;
        const message = `timeout waiting for response ${JSON.stringify(qc.tags)}`;
        this.log.warning(message);
        this.restoreUnterminatedRegExp();
        if (this.retryAttempts < qc.retryAttempts) {
            this.retryAttempts++;
            this.log.info('retrying command');
            this.sendNextCommand(qc);
        }
        else {
            qc.reject(new TransportError(message, 'sendTimeout'));
            this.sendNextCommand();
        }
    }
    suspendTimeoutCallback() {
        this._suspendType = undefined;
        this.timeouts.set(this.sendNextCommand);
        this.timeouts.set(this.processReceivedData);
        if (!this.idleTimeout.isRunning && this.options.idleTimeoutMs > 0) {
            this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
        }
    }
}
